import '@fontsource-variable/ibm-plex-sans/index.css';
import '@fontsource/ibm-plex-mono/400.css';
import '@fontsource/ibm-plex-mono/500.css';
import './styles/tokens.css';
import './styles/base.css';
import './styles/components.css';
import './styles/shell.css';
import './styles/pages.css';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRootRoute, createRoute, createRouter, Navigate, Outlet, RouterProvider, useRouterState } from '@tanstack/react-router';
import { MotionConfig } from 'motion/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { AppShell } from './components/AppShell.tsx';
import { ToastProvider } from './components/ui.tsx';
import { ApiError } from './lib/api.ts';
import { registerServiceWorker } from './lib/pwa.ts';
import { useMe } from './lib/session.ts';
import { Access } from './pages/Access.tsx';
import { Approvals } from './pages/Approvals.tsx';
import { Attendance } from './pages/Attendance.tsx';
import { attendanceSearch } from './pages/AttendanceReport.tsx';
import { Audit } from './pages/Audit.tsx';
import { Canteen } from './pages/Canteen.tsx';
import { CheckIn } from './pages/CheckIn.tsx';
import { Company } from './pages/Company.tsx';
import { Components } from './pages/Components.tsx';
import { Cooperative } from './pages/Cooperative.tsx';
import { Dashboard } from './pages/Dashboard.tsx';
import { Developer } from './pages/Developer.tsx';
import { DeviceDetail } from './pages/DeviceDetail.tsx';
import { Devices } from './pages/Devices.tsx';
import { Interaction } from './pages/Interaction.tsx';
import { Kiosk } from './pages/Kiosk.tsx';
import { LegacyAuthorize } from './pages/LegacyAuthorize.tsx';
import { Login, ResetPassword } from './pages/Login.tsx';
import { MyPayslips, Payslip } from './pages/Payslip.tsx';
import { MyTime } from './pages/MyTime.tsx';
import { NotFound } from './pages/NotFound.tsx';
import { Org } from './pages/Org.tsx';
import { Payroll } from './pages/Payroll.tsx';
import { PayrollRun } from './pages/PayrollRun.tsx';
import { People } from './pages/People.tsx';
import { Person, MyProfile } from './pages/Person.tsx';
import { Roster } from './pages/Roster.tsx';
import { Settings } from './pages/Settings.tsx';
import { Simulation, simulationSearch } from './pages/Simulation.tsx';
import { ThrRun } from './pages/ThrRun.tsx';
import { GoodsPeriod, GoodsPeriods, InvoicePage } from './pages/Goods.tsx';
import { Timesheet } from './pages/Timesheet.tsx';
import { printSearch, recapSearch, TimesheetPrint, TimesheetRecap } from './pages/TimesheetPrint.tsx';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 2,
      refetchOnWindowFocus: false,
      staleTime: 15_000,
    },
  },
});

function Splash() {
  return (
    <div style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center' }}>
      <span className="spinner" style={{ color: 'var(--ink-3)' }} aria-label="Loading" />
    </div>
  );
}

function AppLayout() {
  const me = useMe();
  const href = useRouterState({ select: (s) => s.location.href });
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  if (me.isLoading) return <Splash />;
  if (!me.data) {
    // While the redirect runs, the router can render this layout once more with
    // the new location. Wrapping that /login URL in another ?next= redirected
    // forever (the URL doubled each time until the tab crashed).
    if (pathname === '/login') return <Splash />;
    return <Navigate to="/login" search={{ next: href }} replace />;
  }
  return <AppShell me={me.data} />;
}

const root = createRootRoute({ component: () => <Outlet />, notFoundComponent: NotFound });
const app = createRoute({ getParentRoute: () => root, id: 'app', component: AppLayout });

const r = (path: string, component: () => React.ReactNode) => createRoute({ getParentRoute: () => app, path, component });

const routeTree = root.addChildren([
  createRoute({ getParentRoute: () => root, path: '/login', component: Login, validateSearch: (s: Record<string, unknown>) => ({ next: typeof s.next === 'string' ? s.next : undefined }) }),
  createRoute({ getParentRoute: () => root, path: '/reset-password', component: ResetPassword, validateSearch: (s: Record<string, unknown>) => ({ token: typeof s.token === 'string' ? s.token : '' }) }),
  createRoute({ getParentRoute: () => root, path: '/auth/interaction/$uid', component: Interaction }),
  createRoute({ getParentRoute: () => root, path: '/kiosk', component: Kiosk }),
  createRoute({
    getParentRoute: () => root,
    path: '/legacy/authorize',
    component: LegacyAuthorize,
    validateSearch: (s: Record<string, unknown>) => ({ client_id: String(s.client_id ?? ''), state: typeof s.state === 'string' ? s.state : undefined, mode: s.mode === 'token' ? ('token' as const) : ('code' as const), error: typeof s.error === 'string' ? s.error : undefined }),
  }),
  app.addChildren([
    r('/', Dashboard),
    r('/check-in', CheckIn),
    r('/approvals', Approvals),
    r('/people', People),
    r('/people/$id', Person),
    r('/me', MyProfile),
    r('/org', Org),
    createRoute({ getParentRoute: () => app, path: '/attendance', component: Attendance, validateSearch: attendanceSearch }),
    r('/attendance/$employeeId', Timesheet),
    createRoute({ getParentRoute: () => app, path: '/attendance/$employeeId/print', component: TimesheetPrint, validateSearch: printSearch }),
    createRoute({ getParentRoute: () => app, path: '/attendance/$employeeId/recap', component: TimesheetRecap, validateSearch: recapSearch }),
    r('/roster', Roster),
    r('/time', MyTime),
    r('/payroll', Payroll),
    r('/payroll/runs/$id', PayrollRun),
    r('/payroll/components', Components),
    createRoute({ getParentRoute: () => app, path: '/payroll/simulate', component: Simulation, validateSearch: simulationSearch }),
    r('/payroll/thr/$id', ThrRun),
    r('/payroll/goods', GoodsPeriods),
    r('/payroll/goods/$periodId', GoodsPeriod),
    r('/invoices/$id', InvoicePage),
    r('/payslips', MyPayslips),
    r('/payslips/$id', Payslip),
    r('/devices', Devices),
    r('/devices/$id', DeviceDetail),
    r('/canteen', Canteen),
    r('/cooperative', Cooperative),
    r('/company', Company),
    r('/settings', Settings),
    r('/developer', Developer),
    r('/access', Access),
    r('/audit', Audit),
  ]),
]);

const router = createRouter({ routeTree, defaultPreload: 'intent', scrollRestoration: true });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

registerServiceWorker({
  navigate: (path) => router.history.push(path),
  onPush: () => void queryClient.invalidateQueries({ queryKey: ['inbox-count'] }),
  beforeNavigate: (fn) => router.subscribe('onBeforeNavigate', (e) => fn(e.toLocation.href)),
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <MotionConfig reducedMotion="user">
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </QueryClientProvider>
    </MotionConfig>
  </StrictMode>,
);
